import { useEffect, useMemo, useState } from "react";
import { App, Button, Checkbox, Drawer, Flex, Select, Space, Table, Typography } from "antd";
import { useTranslation } from "react-i18next";
import {
  useGetProctoringAssignments,
  useSaveProctoringAssignments,
  type ProctoringAssignmentItem,
} from "@hooks";
import { candidateName, examStateTag } from "./CandidateTile";

interface Props {
  examTermId: string;
  open: boolean;
  onClose: () => void;
  getContainer?: () => HTMLElement;
}

/// Term admin assigns each candidate to (at most) one assessor; assessors then see only their candidates.
export const ProctorAssignmentsDrawer = ({ examTermId, open, onClose, getContainer }: Props) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { data, isLoading } = useGetProctoringAssignments(examTermId, open);
  const save = useSaveProctoringAssignments(examTermId);

  const [draft, setDraft] = useState<Record<string, string | null>>({});
  const [selected, setSelected] = useState<string[]>([]);
  const [bulkAssessor, setBulkAssessor] = useState<string | null>(null);
  const [distributeTo, setDistributeTo] = useState<string[]>([]);
  const [onlyUnassigned, setOnlyUnassigned] = useState(true);

  useEffect(() => {
    if (!data) return;
    setDraft(Object.fromEntries(data.candidates.map((c) => [c.examInstanceId, c.assessorId ?? null])));
    setSelected([]);
  }, [data]);

  const assessorOptions = useMemo(
    () => (data?.assessors ?? []).map((a) => ({ value: a.assessorId, label: a.name || a.email })),
    [data]
  );

  const loadCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    Object.values(draft).forEach((id) => id && (counts[id] = (counts[id] ?? 0) + 1));
    return counts;
  }, [draft]);

  const changes: ProctoringAssignmentItem[] = (data?.candidates ?? [])
    .filter((c) => (c.assessorId ?? null) !== (draft[c.examInstanceId] ?? null))
    .map((c) => ({ examInstanceId: c.examInstanceId, assessorId: draft[c.examInstanceId] ?? null }));

  const applyBulk = () => {
    setDraft((prev) => ({ ...prev, ...Object.fromEntries(selected.map((id) => [id, bulkAssessor])) }));
  };

  const distribute = () => {
    if (distributeTo.length === 0 || !data) return;
    const targets = data.candidates.filter((c) => !onlyUnassigned || !draft[c.examInstanceId]);
    const next = { ...draft };
    const load = Object.fromEntries(distributeTo.map((id) => [id, onlyUnassigned ? loadCounts[id] ?? 0 : 0]));
    targets.forEach((c) => {
      const least = distributeTo.reduce((a, b) => (load[b] < load[a] ? b : a));
      next[c.examInstanceId] = least;
      load[least] += 1;
    });
    setDraft(next);
  };

  const onSave = () =>
    save.mutate(changes, {
      onSuccess: () => message.success(t("proctoring.saved")),
    });

  return (
    <Drawer
      title={t("proctoring.assignments")}
      open={open}
      onClose={onClose}
      width={760}
      getContainer={getContainer}
      extra={
        <Button type="primary" onClick={onSave} disabled={changes.length === 0} loading={save.isPending} data-testid="assignments-save">
          {t("proctoring.save")} {changes.length > 0 && `(${changes.length})`}
        </Button>
      }
    >
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        <Flex vertical gap={6} style={{ padding: 12, border: "1px dashed #d9d9d9", borderRadius: 8 }}>
          <Typography.Text type="secondary">{t("proctoring.distributeHint")}</Typography.Text>
          <Flex gap={8} wrap="wrap" align="center">
            <Select
              mode="multiple"
              style={{ minWidth: 320, flex: 1 }}
              options={assessorOptions}
              value={distributeTo}
              onChange={setDistributeTo}
              placeholder={t("proctoring.assessor")}
            />
            <Checkbox checked={onlyUnassigned} onChange={(e) => setOnlyUnassigned(e.target.checked)}>
              {t("proctoring.onlyUnassigned")}
            </Checkbox>
            <Button onClick={distribute} disabled={distributeTo.length === 0}>
              {t("proctoring.distribute")}
            </Button>
          </Flex>
        </Flex>

        <Flex gap={8} align="center">
          <Select
            allowClear
            style={{ width: 280 }}
            options={assessorOptions}
            value={bulkAssessor}
            onChange={(v) => setBulkAssessor(v ?? null)}
            placeholder={t("proctoring.unassigned")}
          />
          <Button onClick={applyBulk} disabled={selected.length === 0}>
            {t("proctoring.assignments")} ({selected.length})
          </Button>
        </Flex>

        <Table
          size="small"
          rowKey="examInstanceId"
          loading={isLoading}
          dataSource={data?.candidates ?? []}
          pagination={false}
          scroll={{ y: "calc(100vh - 380px)" }}
          rowSelection={{ selectedRowKeys: selected, onChange: (keys) => setSelected(keys as string[]) }}
          columns={[
            { title: t("proctoring.candidates"), render: (_, c) => candidateName(c) },
            { title: "", width: 150, render: (_, c) => examStateTag(c.examInstanceState, t) },
            {
              title: t("proctoring.assessor"),
              width: 260,
              render: (_, c) => (
                <Select
                  allowClear
                  size="small"
                  style={{ width: "100%" }}
                  data-testid={`assign-${c.examInstanceId}`}
                  options={assessorOptions}
                  value={draft[c.examInstanceId] ?? undefined}
                  placeholder={t("proctoring.unassigned")}
                  onChange={(v) => setDraft((prev) => ({ ...prev, [c.examInstanceId]: v ?? null }))}
                />
              ),
            },
          ]}
        />

        <Flex gap={6} wrap="wrap">
          {(data?.assessors ?? []).map((a) => (
            <Typography.Text key={a.assessorId} type="secondary">
              {a.name}: {loadCounts[a.assessorId] ?? 0}
            </Typography.Text>
          ))}
        </Flex>
      </Space>
    </Drawer>
  );
};
